import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../context/AppIdContext';
import '../style/dashboard.css'; // Reusing existing dashboard styles
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothWiseSurvey = () => {
    const navigate = useNavigate();
    const { appId, language } = useAppId();
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            // Page titles
            title: "Booth Wise Survey",
            subtitle: "Overview of survey progress per booth.",
            
            // Search
            searchPlaceholder: "Search Booth No...",
            
            // Messages
            loading: "Loading Data...",
            noRecordsFound: "No booths found matching",
            
            // Labels
            boothNo: "Booth No. :",
            voter: "Voter :",
            survey: "Survey :",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say"
        },
        
        hi: {
            // Page titles
            title: "बूथ वार सर्वेक्षण",
            subtitle: "प्रति बूथ सर्वेक्षण प्रगति का अवलोकन।",
            
            // Search
            searchPlaceholder: "बूथ नंबर खोजें...",
            
            // Messages
            loading: "डेटा लोड हो रहा है...",
            noRecordsFound: "कोई बूथ नहीं मिला",
            
            // Labels
            boothNo: "बूथ नं :",
            voter: "मतदाता :",
            survey: "सर्वेक्षण :",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "नहीं कह सकते"
        },
        
        gu: {
            // Page titles
            title: "બૂથ વાઈઝ સર્વે",
            subtitle: "પ્રતિ બૂથ સર્વે પ્રગતિની ઝાંખી.",
            
            // Search
            searchPlaceholder: "બૂથ નંબર શોધો...",
            
            // Messages
            loading: "ડેટા લોડ થઈ રહ્યો છે...",
            noRecordsFound: "કોઈ બૂથ મળ્યા નથી",
            
            // Labels
            boothNo: "બૂથ નં :",
            voter: "મતદાર :",
            survey: "સર્વે :",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકતા નથી"
        }
    };

    const txt = t[lang];
    
    const [booths, setBooths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [totalSurveyed, setTotalSurveyed] = useState(0);

    useEffect(() => {
        if (appId) {
            fetchData();
        }
    }, [appId]);

    const fetchData = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_booth_wise_survey_dash`, {
                app_id: appId
            });

            if (response.data.success) {
                const result = response.data.data.result || [];
                setBooths(result);
                
                // Calculate total surveyed across all booths for the footer
                const total = result.reduce((sum, booth) => sum + (booth.total_survey || 0), 0);
                setTotalSurveyed(total);
            }
        } catch (error) {
            console.error("Error fetching booth wise survey:", error);
        } finally {
            setLoading(false);
        }
    };

    // --- NEW: Navigation Handler ---
    const handleCardClick = (boothNo) => {
        // Change '/booth_survey_details' to the actual route of your target page
        navigate('/dis_booth_wise_survey_voter', { state: { booth_no: boothNo } });
    };

    // Filter Logic (Search by Booth No)
    const filteredBooths = booths.filter(booth => 
        String(booth.booth_no).includes(searchTerm)
    );

    return (
        <div className="app-container dashboard-page">
            <div className='dashboard-content container-fluid p-4'>
                
                {/* --- Header Component Integrated Here --- */}
                <Header 
                    title={txt.title} 
                    
                />

                {/* --- Search Bar --- */}
                <div className="row fade-in-up mt-3" style={{ animationDelay: '0.1s' }}>
                    <div className="col-12">
                        <div className="d-flex justify-content-end align-items-center flex-wrap gap-3">
                            <div className="search-box-wrapper">
                                <svg className="search-icon" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input 
                                    type="text" 
                                    className="header-search-input" 
                                    placeholder={txt.searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- Content List --- */}
                <div className="container-fluid mt-4" style={{ flex: 1 }}>
                    {loading ? (
                        <div className="text-center p-5 text-muted">{txt.loading}</div>
                    ) : (
                        <div className="row g-4">
                            {filteredBooths.length > 0 ? (
                                filteredBooths.map((booth) => (
                                    <div key={booth.booth_no} className="col-12 col-md-6 col-xl-4 fade-in-up">
                                        {/* ADDED: onClick handler and cursor pointer */}
                                        <div 
                                            className="booth-survey-card h-100" 
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleCardClick(booth.booth_no)}
                                        >
                                            
                                            {/* Header: Booth No & Survey Count */}
                                            <div className="booth-card-header">
                                                <div className="booth-title-group">
                                                    <h3 className="booth-title">{txt.boothNo} {booth.booth_no}</h3>
                                                    <span className="voter-sub">{txt.voter} {booth.total_voter}</span>
                                                </div>
                                                <div className="survey-badge">
                                                    {txt.survey} {booth.total_survey}
                                                </div>
                                            </div>

                                            {/* Stats Grid */}
                                            <div className="booth-stats-grid">
                                                <div className="b-stat-box bg-green-light">
                                                    <span className="b-stat-label">{txt.positive}</span>
                                                    <span className="b-stat-val text-green">{booth.P || 0}</span>
                                                </div>
                                                <div className="b-stat-box bg-red-light">
                                                    <span className="b-stat-label">{txt.negative}</span>
                                                    <span className="b-stat-val text-red">{booth.N || 0}</span>
                                                </div>
                                                <div className="b-stat-box bg-orange-light">
                                                    <span className="b-stat-label">{txt.doubtful}</span>
                                                    <span className="b-stat-val text-orange">{booth.D || 0}</span>
                                                </div>
                                                <div className="b-stat-box bg-blue-light">
                                                    <span className="b-stat-label">{txt.cantSay}</span>
                                                    <span className="b-stat-val text-blue">{booth.C || 0}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="col-12 text-center text-muted p-4">
                                    {txt.noRecordsFound} "{searchTerm}"
                                </div>
                            )}
                        </div>
                    )}
                </div>

                
            </div>

            <style jsx>{`
                /* Search Bar Styling */
                .search-box-wrapper {
                    position: relative;
                    width: 100%;
                    max-width: 300px;
                }
                .header-search-input {
                    width: 100%;
                    padding: 10px 10px 10px 40px;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                    outline: none;
                    font-size: 14px;
                    background-color: white;
                    color: #1e293b;
                    transition: border-color 0.2s;
                }
                .header-search-input:focus {
                    border-color: #3b82f6;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }
                .search-icon {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #94a3b8;
                }

                /* Booth Card Styles */
                .booth-survey-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
                    border: 1px solid #f1f5f9;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease;
                    /* Left Border Stripe like in image */
                    border-left: 4px solid #1e3a8a; 
                }
                .booth-survey-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);
                }

                /* Card Header */
                .booth-card-header {
                    padding: 16px 20px;
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    border-bottom: 1px solid #f8fafc;
                }
                .booth-title {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0 0 4px 0;
                }
                .voter-sub {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 500;
                }
                .survey-badge {
                    font-size: 14px;
                    font-weight: 700;
                    color: #1e293b;
                }

                /* Stats Grid */
                .booth-stats-grid {
                    padding: 15px 20px;
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 10px;
                }
                .b-stat-box {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 10px 5px;
                    border-radius: 8px;
                    text-align: center;
                }
                .b-stat-label {
                    font-size: 11px;
                    font-weight: 600;
                    margin-bottom: 4px;
                    color: #334155;
                }
                .b-stat-val {
                    font-size: 16px;
                    font-weight: 800;
                }

                /* Background Colors for Stats */
                .bg-green-light { background-color: #f0fdf4; }
                .bg-red-light { background-color: #fef2f2; }
                .bg-orange-light { background-color: #fff7ed; }
                .bg-blue-light { background-color: #eff6ff; }

                /* Text Colors */
                .text-green { color: #166534; }
                .text-red { color: #991b1b; }
                .text-orange { color: #9a3412; }
                .text-blue { color: #1e40af; }

                /* Footer Info inside Card */
                .booth-footer-info {
                    padding: 10px 20px;
                    background-color: #f8fafc;
                    border-top: 1px solid #f1f5f9;
                    font-size: 12px;
                    color: #64748b;
                    text-align: right;
                }

                /* Page Footer */
                .footer-bar {
                    background-color: white;
                    padding: 15px 25px;
                    border-top: 1px solid #e2e8f0;
                    position: sticky;
                    bottom: 0;
                    display: flex;
                    justify-content: flex-start;
                    box-shadow: 0 -4px 6px -1px rgba(0,0,0,0.02);
                    z-index: 50;
                }
                .footer-text {
                    font-size: 14px;
                    color: #475569;
                    background: #f1f5f9;
                    padding: 8px 18px;
                    border-radius: 20px;
                    border: 1px solid #e2e8f0;
                    font-weight: 600;
                }

                /* =========================================
                   RESPONSIVE MEDIA QUERIES (MOBILE FIXES)
                   ========================================= */
                @media (max-width: 768px) {
                    .search-box-wrapper {
                        max-width: 100%; /* Full width on mobile */
                        margin-top: 8px;
                    }
                    .page-header {
                        align-items: flex-start !important;
                    }
                    .header-content {
                        margin-bottom: 8px;
                    }
                    .booth-card-header {
                        padding: 14px 16px;
                    }
                    .booth-stats-grid {
                        padding: 14px 16px;
                        gap: 8px;
                    }
                }

                @media (max-width: 480px) {
                    .booth-title {
                        font-size: 15px;
                    }
                    .voter-sub {
                        font-size: 12px;
                    }
                    .survey-badge {
                        font-size: 13px;
                    }
                    .b-stat-box {
                        padding: 8px 4px;
                    }
                    .b-stat-label {
                        font-size: 9px;
                        letter-spacing: -0.5px;
                        white-space: nowrap;
                    }
                    .b-stat-val {
                        font-size: 14px;
                    }
                    .footer-bar {
                        padding: 12px 15px;
                        justify-content: center;
                    }
                }
            `}</style>
        </div>
    );
};

export default BoothWiseSurvey;